<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import VipLevelRow from './VipLevelRow.vue';
import AddVipLevel from './AddVipLevel.vue';
import EditVipLevel from './EditVipLevel.vue';

const store = useStore();
const { t } = useI18n();

const vipLevels = useMapGetter('vipLevels/getVipLevels');
const uiFlags = useMapGetter('vipLevels/getUIFlags');

const addDialogRef = ref(null);
const editDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedVipLevel = ref(null);
const vipLevelToDelete = ref(null);
const searchQuery = ref('');

const isClosingAddDialog = ref(false);
const isClosingEditDialog = ref(false);

// Filtered VIP levels based on search
const filteredVipLevels = computed(() => {
  if (!searchQuery.value) return vipLevels.value;
  const query = searchQuery.value.toLowerCase();
  return vipLevels.value.filter(level =>
    level.name.toLowerCase().includes(query)
  );
});

onMounted(() => {
  store.dispatch('vipLevels/get');
});

const openAddModal = () => {
  addDialogRef.value?.open();
};

const requestCloseAddDialog = () => {
  if (addDialogRef.value) {
    isClosingAddDialog.value = true;
    addDialogRef.value.close();
  }
};

const closeAddModal = () => {
  isClosingAddDialog.value = false;
};

const openEditModal = vipLevel => {
  selectedVipLevel.value = vipLevel;
  editDialogRef.value?.open();
};

const requestCloseEditDialog = () => {
  if (editDialogRef.value) {
    isClosingEditDialog.value = true;
    editDialogRef.value.close();
  }
};

const closeEditModal = () => {
  if (!isClosingEditDialog.value) {
    selectedVipLevel.value = null;
  }
  isClosingEditDialog.value = false;
  selectedVipLevel.value = null;
};

const openDeleteDialog = vipLevel => {
  vipLevelToDelete.value = vipLevel;
  deleteDialogRef.value?.open();
};

const handleCreate = async vipLevelData => {
  try {
    await store.dispatch('vipLevels/create', vipLevelData);
    useAlert(t('VIP_SETTINGS.ADD.API.SUCCESS_MESSAGE'));
    requestCloseAddDialog();
  } catch (error) {
    useAlert(t('VIP_SETTINGS.ADD.API.ERROR_MESSAGE'));
  }
};

const handleUpdate = async payload => {
  try {
    await store.dispatch('vipLevels/update', payload);
    useAlert(t('VIP_SETTINGS.EDIT.API.SUCCESS_MESSAGE'));
    requestCloseEditDialog();
  } catch (error) {
    console.error('VIP update error:', error);
    useAlert(t('VIP_SETTINGS.EDIT.API.ERROR_MESSAGE'));
  }
};

const confirmDelete = async () => {
  if (!vipLevelToDelete.value || uiFlags.value.isDeleting) return;

  try {
    await store.dispatch('vipLevels/delete', vipLevelToDelete.value.id);
    deleteDialogRef.value?.close();
    useAlert(t('VIP_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
    vipLevelToDelete.value = null;
  } catch (error) {
    useAlert(t('VIP_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px; background: #fff; border-bottom: 1px solid #e5e7eb"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('VIP_SETTINGS.HEADER') }}
      </h1>
      <button class="primary-action-btn" @click="openAddModal">
        <span class="i-ph-plus-bold size-3.5" />
        {{ $t('VIP_SETTINGS.ADD.BUTTON_TEXT') }}
      </button>
    </header>

    <!-- Description -->
    <div
      class="px-6 py-3"
      style="background: #fff; border-bottom: 1px solid #e5e7eb"
    >
      <p class="text-sm" style="color: #4b5563">
        {{ $t('VIP_SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search and Filters -->
    <div
      class="px-6 py-3"
      style="background: #fff; border-bottom: 1px solid #e5e7eb"
    >
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-xs">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
            placeholder="Search VIP levels..."
          />
        </div>
        <div class="text-sm self-center" style="color: #6b7280">
          {{ filteredVipLevels.length }} levels
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6" style="background: #fff">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <Spinner />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredVipLevels.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div class="vip-empty-icon">
          <span class="i-lucide-star size-10" style="color: #9ca3af" />
        </div>
        <p
          class="text-sm text-center"
          style="color: #6b7280; margin-bottom: 16px"
        >
          {{ $t('VIP_SETTINGS.LIST.404') }}
        </p>
        <button class="primary-action-btn" @click="openAddModal">
          <span class="i-ph-plus-bold size-3.5" />
          {{ $t('VIP_SETTINGS.ADD.BUTTON_TEXT') }}
        </button>
      </div>

      <!-- VIP Levels List -->
      <div v-else class="space-y-4">
        <VipLevelRow
          v-for="vipLevel in filteredVipLevels"
          :key="vipLevel.id"
          :vip-level="vipLevel"
          @edit="openEditModal"
          @delete="openDeleteDialog"
        />
      </div>
    </div>

    <!-- Add Modal -->
    <Dialog
      ref="addDialogRef"
      width="2xl"
      surface="light"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="closeAddModal"
    >
      <AddVipLevel @submit="handleCreate" @cancel="requestCloseAddDialog" />
    </Dialog>

    <!-- Edit Modal -->
    <Dialog
      ref="editDialogRef"
      width="2xl"
      surface="light"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="closeEditModal"
    >
      <EditVipLevel
        v-if="selectedVipLevel"
        :vip-level="selectedVipLevel"
        @submit="handleUpdate"
        @cancel="requestCloseEditDialog"
      />
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      surface="light"
      :title="$t('VIP_SETTINGS.DELETE.CONFIRM.TITLE')"
      :description="
        vipLevelToDelete
          ? $t('VIP_SETTINGS.DELETE.CONFIRM.MESSAGE', {
              name: vipLevelToDelete.name,
            })
          : ''
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="$t('VIP_SETTINGS.DELETE.BUTTON_TEXT')"
      :cancel-button-label="$t('VIP_SETTINGS.DELETE.CANCEL')"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped>
/* Forced white theme */
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}
.settings-header {
  background: #ffffff;
}

.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.search-input::placeholder {
  color: #9ca3af !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.primary-action-btn:hover {
  background: #1f2937;
}
.primary-action-btn:active {
  transform: scale(0.98);
}

/* Empty state */
.vip-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

/* List readability in forced light theme */
:deep(.vip-row) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.vip-name) {
  color: #111827 !important;
}

:deep(.vip-sub) {
  color: #4b5563 !important;
}

:deep(.vip-level-chip) {
  background: #f3f4f6 !important;
  border: 1px solid #e5e7eb !important;
  color: #374151 !important;
}

/* Sub-component overrides */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(input:not(.reset-base)) {
  background: #fff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
